import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  createResetCspSigninState,
  createSetSelectedCspSessionTypeAction,
} from 'src/app/CspSignin/redux/cspSignin.actions'
import { CspSessionType } from 'src/app/CspSignin/redux/cspSignin.reducer'
import {
  selectCspLoginErrorDetailMapFromState,
  selectCspLoginTopLevelErrorFromState,
  selectCspUser,
  selectHasSentTokenFromState,
  selectIsLookingUpFromState,
  selectIsPostingCspEmulatedSessionsLiveFromState,
  selectIsPostingCspEmulatedSessionsOfflineFromState,
  selectIsPuttingCspEmulatedSessionsVerifyFromState,
  selectIsVerifyingUserFromState,
  selectLookupErrorDetailMapFromState,
  selectLookupTopLevelErrorFromState,
  selectPostingCspEmulatedSessionsLiveErrorDetailMapFromState,
  selectPostingCspEmulatedSessionsLiveTopLevelErrorFromState,
  selectPostingCspEmulatedSessionsOfflineErrorDetailMapFromState,
  selectPostingCspEmulatedSessionsOfflineTopLevelErrorFromState,
  selectPuttingCspEmulatedSessionsVerifyErrorDetailMapFromState,
  selectPuttingCspEmulatedSessionsVerifyTopLevelErrorFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectSelectedCspSessionType,
  selectUserListModelFromState,
  selectUserSessionTokenIdFromState,
  selectVerificationEmailResentFromState,
  selectVerifyEmailTopLevelErrorFromState,
} from 'src/app/CspSignin/redux/cspSignin.selectors'
import { selectIsSigningInFromState } from 'src/app/Signin/redux/signin.selectors'
import CspStartSessionBox from 'src/components/CspStartSessionBox/CspStartSessionBox'
import CspTopBar from 'src/components/CspTopBar/CspTopBar'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SigninForm from 'src/components/SigninForm/SigninForm'
import SvgCustomerSupport from 'src/components/Svgs/SvgCustomerSupport'
import UnauthedBox from 'src/components/UnauthedBox/UnauthedBox'
import VerifyForm from 'src/components/VerifyForm/VerifyForm'
import { Environments } from 'src/configurations'
import {
  AuthTypes,
  CognitoErrorMessages,
  MfaDeliveryMedium,
  UserStatus,
} from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  confirmCognitoSignin,
  createAbandonMfaChallengeAction,
  createClearAuthFlowTypeAction,
  createSetEmail,
  deleteCognitoSignout,
  deleteSignout,
  postAuthCspLogin,
  postCspEmulatedSessionsLive,
  postCspEmulatedSessionsOfflineThenPush,
  postCspLoginIssuerInfo,
  putCspEmulatedSessionsResendThenPush,
  putCspEmulatedSessionsVerifyThenPush,
} from 'src/redux/actions/auth.actions'
import { getCspUser } from 'src/redux/actions/data/users.actions'
import {
  selectAuthTypeFromState,
  selectCspUserListModelFromState,
  selectDefaultCspIssuer,
  selectDidSessionExpireAutomaticallyFromState,
  selectEmailFromState,
  selectIsCspAuthenticatedFromState,
  selectMfaChallengeInitiated,
  selectMfaDeliveryMediumFromState,
  selectMfaDestinationFromState,
  selectNullSafeLocaleFromState,
  selectUserStatusFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const CspSignin: React.FC = () => {
  const email = useSelector(selectEmailFromState)
  const user = useSelector(selectUserListModelFromState)
  const cspUser = useSelector(selectCspUser)
  const userStatus = useSelector(selectUserStatusFromState)
  const isLookingUp = useSelector(selectIsLookingUpFromState)
  const isSigningIn = useSelector(selectIsSigningInFromState)
  const isVerifyingUser = useSelector(selectIsVerifyingUserFromState)
  const verificationEmailResent = useSelector(
    selectVerificationEmailResentFromState,
  )
  const resendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )
  const verifyEmailTopLevelError = useSelector(
    selectVerifyEmailTopLevelErrorFromState,
  )
  const lookupTopLevelError = useSelector(selectLookupTopLevelErrorFromState)
  const lookupErrorDetailMap = useSelector(selectLookupErrorDetailMapFromState)
  const cspLoginTopLevelError = useSelector(
    selectCspLoginTopLevelErrorFromState,
  )
  const cspLoginErrorDetailMap = useSelector(
    selectCspLoginErrorDetailMapFromState,
  )
  const didSessionExpireAutomatically = useSelector(
    selectDidSessionExpireAutomaticallyFromState,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const selectedCspSessionType = useSelector(selectSelectedCspSessionType)
  const isPostingCspEmulatedSessionsOffline = useSelector(
    selectIsPostingCspEmulatedSessionsOfflineFromState,
  )
  const postCspEmulatedSessionsOfflineTopLevelError = useSelector(
    selectPostingCspEmulatedSessionsOfflineTopLevelErrorFromState,
  )
  const postCspEmulatedSessionsOfflineErrorDetailMap = useSelector(
    selectPostingCspEmulatedSessionsOfflineErrorDetailMapFromState,
  )
  const isPostingCspEmulatedSessionsLive = useSelector(
    selectIsPostingCspEmulatedSessionsLiveFromState,
  )
  const postCspEmulatedSessionsLiveTopLevelError = useSelector(
    selectPostingCspEmulatedSessionsLiveTopLevelErrorFromState,
  )
  const postCspEmulatedSessionsLiveErrorDetailMap = useSelector(
    selectPostingCspEmulatedSessionsLiveErrorDetailMapFromState,
  )
  const isPutingCspEmulatedSessionsVerify = useSelector(
    selectIsPuttingCspEmulatedSessionsVerifyFromState,
  )
  const putCspEmulatedSessionsVerifyTopLevelError = useSelector(
    selectPuttingCspEmulatedSessionsVerifyTopLevelErrorFromState,
  )
  const putCspEmulatedSessionsVerifyErrorDetailMap = useSelector(
    selectPuttingCspEmulatedSessionsVerifyErrorDetailMapFromState,
  )
  const defaultCspIssuer = useSelector(selectDefaultCspIssuer)
  const userSessionTokenId = useSelector(selectUserSessionTokenIdFromState)
  const hasSentToken = useSelector(selectHasSentTokenFromState)
  const env = useSelector(selectEnvironmentFromState)
  const mfaChallengeInitiated = useSelector(selectMfaChallengeInitiated)
  const authType = useSelector(selectAuthTypeFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const cspUserListModel = useSelector(selectCspUserListModelFromState)
  const { analytics } = useAnalyticsContext()
  const mfaDestination = useSelector(selectMfaDestinationFromState)
  const mfaDeliveryMedium = useSelector(selectMfaDeliveryMediumFromState)

  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(createClearAuthFlowTypeAction())
  }, [])

  const dispatchPostAuthCspLogin = (
    email: string,
    password?: string,
    newCode?: boolean,
  ): void => {
    dispatch(postAuthCspLogin(email, password, newCode))
  }

  const dispatchSetEmail = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const dispatchDeleteSignout = (): void => {
    if (authType === AuthTypes.auth0) {
      dispatch(deleteSignout(false, analytics, true, undefined))
    } else {
      dispatch(deleteCognitoSignout(false, analytics, true))
    }
  }

  const dispatchSetSelectedCspSessionType = (val?: CspSessionType): void => {
    dispatch(createSetSelectedCspSessionTypeAction(val))
  }

  const dispatchPostCspEmulatedSessionsOffline = (
    email: string,
    password?: string,
  ): void => {
    if (defaultCspIssuer?.id !== undefined) {
      dispatch(postCspEmulatedSessionsOfflineThenPush(email))
    } else {
      if (cspUserListModel) {
        dispatch(postCspLoginIssuerInfo(cspUserListModel.email, password))
      }
      dispatch(postCspEmulatedSessionsOfflineThenPush(email))
    }
  }

  const dispatchPostCspEmulatedSessionsLive = (
    email: string,
    password?: string,
  ): void => {
    if (defaultCspIssuer?.id !== undefined) {
      dispatch(postCspEmulatedSessionsLive(email))
    } else {
      if (cspUserListModel) {
        dispatch(postCspLoginIssuerInfo(cspUserListModel.email, password))
      }
      dispatch(postCspEmulatedSessionsLive(email))
    }
  }

  const dispatchPutCspEmulatedSessionsVerify = (
    verificationCode: string,
  ): void => {
    if (userSessionTokenId !== undefined) {
      dispatch(
        putCspEmulatedSessionsVerifyThenPush(
          userSessionTokenId,
          verificationCode,
        ),
      )
    }
  }

  const dispatchPutCspEmulatedSessionsResend = (): void => {
    if (userSessionTokenId !== undefined) {
      dispatch(putCspEmulatedSessionsResendThenPush(userSessionTokenId))
    }
  }

  const dispatchCreateResetCspSigninState = (): void => {
    dispatch(createResetCspSigninState())
  }

  const dispatchAbandonMfaChallenge = (): void => {
    dispatch(createAbandonMfaChallengeAction())
  }

  const dispatchConfirmCognitoSignin = (code: string): void => {
    dispatch(confirmCognitoSignin(code, false, true))
  }

  const [password, setPassword] = useState<string>('')

  useEffect((): void => {
    dispatch(createSetSelectedCspSessionTypeAction(undefined))
  }, [isCspAuthenticated])

  useEffect((): void => {
    if (user?.id) {
      dispatch(getCspUser(user.id))
    }
  }, [user?.id])

  const signinFormTopLevelError = (): string | undefined => {
    if (lookupTopLevelError || cspLoginTopLevelError) {
      if (authType === AuthTypes.cognito) {
        switch (cspLoginTopLevelError) {
          case CognitoErrorMessages.incorrectUsernameOrPassword:
            return translateSingleString(
              locale,
              'Unable to log in, please review your email/password',
            )
          case CognitoErrorMessages.passwordResetRequiredForTheUser:
            return translateSingleString(
              locale,
              "For security purposes, your password needs to be reset. Please click the 'Forgot Your Password?' link below to update your credentials.",
            )
          default:
            return lookupTopLevelError || cspLoginTopLevelError
        }
      } else {
        return lookupTopLevelError || cspLoginTopLevelError
      }
    } else {
      return undefined
    }
  }

  const customerSupportImage = (
    <SvgCustomerSupport
      css={css`
        transform: translate(50px, -70px) rotate(10deg);
      `}
    />
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        min-height: 100vh;
        width: 100%;
        background-color: ${themeConstants.colors.font_primary};
      `}
    >
      <CspTopBar
        deleteSignout={dispatchDeleteSignout}
        selectedCspSessionType={selectedCspSessionType}
        resetCspSigninState={dispatchCreateResetCspSigninState}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-top: 60px;
          padding-bottom: 180px;

          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding: 40px 10px;
          `)}
        `}
      >
        {mfaChallengeInitiated && !isCspAuthenticated ? (
          <UnauthedBox topRightImage={customerSupportImage}>
            <VerifyForm
              email={email}
              password={password}
              title={<Trns source={'Verify Code'} />}
              subtitle={
                <p>
                  <Trns
                    source={
                      'Enter the 6-digit verification code sent to ${yourEmailOrPhone}'
                    }
                    expressions={{
                      yourEmailOrPhone: (
                        <p
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )};
                          `}
                        >
                          {
                            mfaDeliveryMedium === MfaDeliveryMedium.Email
                              ? mfaDestination
                              : '(•••) •••-' + mfaDestination?.slice(-4)
                            // ^ must manually format phone number since its not valid (format given is '+*******1234')
                          }
                        </p>
                      ),
                    }}
                  />
                </p>
              }
              buttonText={<Trns source={'Verify Code'} />}
              isVerifyingUser={isVerifyingUser || isSigningIn}
              verifyTopLevelError={verifyEmailTopLevelError}
              resendVerificationTopLevelError={resendVerificationTopLevelError}
              verificationResent={verificationEmailResent}
              setStatus={dispatchAbandonMfaChallenge}
              confirmCognitoSignin={dispatchConfirmCognitoSignin}
              postCognitoResendMfaCode={dispatchPostAuthCspLogin}
            />
          </UnauthedBox>
        ) : (
          <>
            {userStatus !== UserStatus.NotVerified && !isCspAuthenticated && (
              <UnauthedBox topRightImage={customerSupportImage}>
                {env === Environments.Prod ? (
                  <GoogleReCaptchaProvider
                    reCaptchaKey={'6Lda69MgAAAAALa0nw9lUWo7NBrvsQzwwAvwEAuL'}
                  >
                    <SigninForm
                      email={email}
                      setEmail={dispatchSetEmail}
                      password={password}
                      setPassword={setPassword}
                      postSignin={dispatchPostAuthCspLogin}
                      isSigningIn={isLookingUp || isSigningIn}
                      errorDetailMap={
                        lookupErrorDetailMap || cspLoginErrorDetailMap
                      }
                      topLevelError={signinFormTopLevelError()}
                      didSessionExpireAutomatically={
                        didSessionExpireAutomatically
                      }
                      isCsp={true}
                    />
                  </GoogleReCaptchaProvider>
                ) : (
                  <SigninForm
                    email={email}
                    setEmail={dispatchSetEmail}
                    password={password}
                    setPassword={setPassword}
                    postSignin={dispatchPostAuthCspLogin}
                    isSigningIn={isLookingUp || isSigningIn}
                    errorDetailMap={
                      lookupErrorDetailMap || cspLoginErrorDetailMap
                    }
                    topLevelError={signinFormTopLevelError()}
                    didSessionExpireAutomatically={
                      didSessionExpireAutomatically
                    }
                    isCsp={true}
                  />
                )}
              </UnauthedBox>
            )}
          </>
        )}
        {isCspAuthenticated && (
          <Fragment>
            <CspStartSessionBox
              user={user}
              cspUser={cspUser}
              password={password}
              selectedCspSessionType={selectedCspSessionType}
              setSelectedCspSessionType={dispatchSetSelectedCspSessionType}
              postCspEmulatedSessionsOffline={
                dispatchPostCspEmulatedSessionsOffline
              }
              isPostingCspEmulatedSessionsOffline={
                isPostingCspEmulatedSessionsOffline
              }
              hasSentToken={hasSentToken}
              putCspEmulatedSessionsResend={
                dispatchPutCspEmulatedSessionsResend
              }
              postCspEmulatedSessionsOfflineTopLevelError={
                postCspEmulatedSessionsOfflineTopLevelError
              }
              postCspEmulatedSessionsOfflineErrorDetailMap={
                postCspEmulatedSessionsOfflineErrorDetailMap
              }
              postCspEmulatedSessionsLive={dispatchPostCspEmulatedSessionsLive}
              isPostingCspEmulatedSessionsLive={
                isPostingCspEmulatedSessionsLive
              }
              postCspEmulatedSessionsLiveTopLevelError={
                postCspEmulatedSessionsLiveTopLevelError
              }
              postCspEmulatedSessionsLiveErrorDetailMap={
                postCspEmulatedSessionsLiveErrorDetailMap
              }
              defaultCspIssuer={defaultCspIssuer}
              putCspEmulatedSessionsVerify={
                dispatchPutCspEmulatedSessionsVerify
              }
              isPuttingCspEmulatedSessionsVerify={
                isPutingCspEmulatedSessionsVerify
              }
              putCspEmulatedSessionsVerifyTopLevelError={
                putCspEmulatedSessionsVerifyTopLevelError
              }
              putCspEmulatedSessionsVerifyErrorDetailMap={
                putCspEmulatedSessionsVerifyErrorDetailMap
              }
            />
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.white,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 20px;
                text-align: center;
              `}
            >
              <Trns
                source={
                  "If you're having trouble at any point, visit our ${resourceCenter} for support and contact options."
                }
                expressions={{
                  resourceCenter: <ResourceCenterLink underline />,
                }}
              />
            </span>
          </Fragment>
        )}
      </div>
    </div>
  )
}

export default CspSignin
